import { manaMeta, parseManaCost } from "@/lib/mana";
import type { SourceKey } from "@/lib/types";
import { cn } from "@/lib/utils";

const localKeys = new Set<string>(["W", "U", "B", "R", "G", "C"]);

/**
 * Every card symbol (hybrid, phyrexian, generic, {T}, {E}, ...) is bundled
 * locally (scripts/fetch-card-symbols.mjs) — no runtime requests to Scryfall.
 */
const bundledSymbols: Record<string, string> = Object.fromEntries(
  Object.entries(
    import.meta.glob<string>("../assets/card-symbols/*.svg", {
      eager: true,
      query: "?url",
      import: "default",
    }),
  ).map(([path, url]) => [path.slice(path.lastIndexOf("/") + 1, -".svg".length), url]),
);

const symbolAliases: Record<string, string> = { "½": "HALF", "∞": "INFINITY" };

function symbolUrl(symbol: string): string | undefined {
  if (localKeys.has(symbol)) {
    return manaMeta[symbol as SourceKey].symbol;
  }
  const key = symbol.replace(/\//g, "").toUpperCase();
  return bundledSymbols[symbolAliases[symbol] ?? key];
}

export function ManaSymbol({ symbol, className }: { symbol: string; className?: string }) {
  const src = symbolUrl(symbol);
  if (!src) {
    return (
      <span
        title={`{${symbol}}`}
        className={cn(
          "inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#cac5c0] text-[0.55rem] font-bold leading-none text-black shadow-[0_1px_0_rgba(0,0,0,0.6)]",
          className,
        )}
      >
        {symbol}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={`{${symbol}}`}
      title={`{${symbol}}`}
      loading="lazy"
      draggable={false}
      className={cn(
        "inline-block h-4 w-4 rounded-full shadow-[0_1px_0_rgba(0,0,0,0.6)]",
        className,
      )}
    />
  );
}

export function ManaCost({
  cost,
  className,
  symbolClassName,
}: {
  cost: string;
  className?: string;
  symbolClassName?: string;
}) {
  const faces = cost.split(" // ");
  return (
    <span className={cn("inline-flex flex-wrap items-center gap-0.5", className)}>
      {faces.map((face, faceIndex) => (
        <span key={faceIndex} className="inline-flex items-center gap-0.5">
          {faceIndex > 0 && <span className="mx-1 text-muted-foreground">//</span>}
          {parseManaCost(face).map((symbol, index) => (
            <ManaSymbol key={`${symbol}-${index}`} symbol={symbol} className={symbolClassName} />
          ))}
        </span>
      ))}
    </span>
  );
}

/** Replace {X} symbols inside oracle text with inline icons. */
export function OracleText({
  text,
  className,
  symbolClassName,
}: {
  text: string;
  className?: string;
  symbolClassName?: string;
}) {
  return (
    <div className={cn("space-y-1.5", className)}>
      {text.split("\n").map((line, lineIndex) => (
        <p key={lineIndex}>
          {line.split(/(\{[^}]+\})/g).map((part, index) => {
            const match = /^\{([^}]+)\}$/.exec(part);
            return match ? (
              <ManaSymbol
                key={index}
                symbol={match[1] ?? ""}
                className={cn("mx-px h-3.5 w-3.5 align-[-2px]", symbolClassName)}
              />
            ) : (
              <span key={index}>{part}</span>
            );
          })}
        </p>
      ))}
    </div>
  );
}
